import { useRef, useLayoutEffect } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { Mode, ConnectivityPlugin } from '@atlaskit/editor-plugin-connectivity';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { sendableSteps } from '@atlaskit/prosemirror-collab';

import { expVal } from '@confluence/feature-experiments';

import { resyncOnline } from '../../pm-plugins/resync-online';
import { editorOfflineStoragePluginKey } from '../../pm-plugins/main';
import { syncWithOfflineStorageUnthrottled } from '../../pm-plugins/syncWithOfflineStorage';
import type { StepsDatabase } from '../../stepsDatabase';

import { useCreateOfflineVersion } from './useCreateOfflineVersion';

/**
 * `usePluginStateEffect` has a debounce - we avoid that here to ensure we
 * catch all states on a critical piece of code
 *
 * @param api
 * @param cb
 */
const useConnectivityEffect = (
	api: PublicPluginAPI<[ConnectivityPlugin]> | undefined,
	cb: (props: { prevMode: Mode | undefined; nextMode: Mode | undefined }) => void,
) => {
	// Get the latest effect similar to `usePluginStateEffect` (no debounce)
	const latestEffect = useRef<typeof cb | undefined>(cb);
	useLayoutEffect(() => {
		latestEffect.current = cb;
		return () => {
			latestEffect.current = undefined;
		};
	}, [cb]);

	useLayoutEffect(() => {
		return api?.connectivity?.sharedState.onChange(({ prevSharedState, nextSharedState }) => {
			latestEffect.current?.({ prevMode: prevSharedState?.mode, nextMode: nextSharedState?.mode });
		});
	}, [api]);
};

/**
 * Resync the editor online
 */
export const useResyncOnline = ({
	api,
	editorView,
	stepsDatabase,
	broadcastUpdate,
	contentId,
}: {
	api: PublicPluginAPI<[AnalyticsPlugin, ConnectivityPlugin]> | undefined;
	editorView: EditorView;
	stepsDatabase: StepsDatabase;
	broadcastUpdate: () => void;
	contentId: string | undefined;
}) => {
	const createOfflineVersion = useCreateOfflineVersion({ contentId, api });

	useConnectivityEffect(api, ({ prevMode, nextMode }) => {
		const { status, disabled } = editorOfflineStoragePluginKey.getState(editorView.state) ?? {};
		if (status !== 'ready' || disabled) {
			return;
		}
		if (prevMode === 'offline' && nextMode === 'online') {
			// Try to prioritise the "active" tab by delaying the inactive tabs, this is to minimise
			// our chance of disrupting the user by the reset
			if (document && !document?.hasFocus?.()) {
				// Give the active document ~1s to connect
				setTimeout(
					() =>
						resyncOnline(api, stepsDatabase, editorView, false, {
							createOfflineVersion,
						}),
					document?.visibilityState === 'visible' ? 500 : 1000,
				);
			} else {
				void resyncOnline(api, stepsDatabase, editorView, true, {
					createOfflineVersion,
				});
			}
		} else if (nextMode === 'offline') {
			if (expVal('platform_editor_offline_editing_idx_db_validation', 'isEnabled', false)) {
				void stepsDatabase.validateIndexedDBAccess().then((isIndexedDBAvailable) => {
					if (isIndexedDBAvailable) {
						void stepsDatabase
							.clear(true)
							.then(async (cleared) => {
								if (cleared) {
									await stepsDatabase.releaseSyncLock?.();
								}
							})
							.finally(() => {
								// Resync the db with the current state of the unconfirmed steps
								const { version, steps } = sendableSteps(editorView.state) ?? {};
								if (version !== undefined && steps !== undefined) {
									void syncWithOfflineStorageUnthrottled({
										editorView,
										stepsDatabase,
										version,
										steps,
										broadcast: broadcastUpdate,
									});
								}
							});
					}
				});
			} else {
				void stepsDatabase
					.clear(true)
					.then(async (cleared) => {
						if (cleared) {
							await stepsDatabase.releaseSyncLock?.();
						}
					})
					.finally(() => {
						// Resync the db with the current state of the unconfirmed steps
						const { version, steps } = sendableSteps(editorView.state) ?? {};
						if (version !== undefined && steps !== undefined) {
							void syncWithOfflineStorageUnthrottled({
								editorView,
								stepsDatabase,
								version,
								steps,
								broadcast: broadcastUpdate,
							});
						}
					});
			}
		}
	});
};
